/* eslint-disable no-restricted-globals */
import Router from "next/router";
import React, { useEffect, useState } from "react";
import { defineMessages, injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { ROUTES } from "shared/constants";
import {
  startUniversalLoading,
  stopUniversalLoading,
} from "shared/data/actions/loader";
import useResult from "shared/hooks/useResult";
import { scrollTo } from "shared/lib/functions";
import { setInitialRequestorConfig } from "shared/lib/requestorFunctions";
import { formattedMessageParams } from "shared/messages";

import BookingForm from "@components/BookingForm/BookingForm";
import BookingFormSubmit from "@components/BookingForm/BookingFormSubmit";
import BrandsModal from "@components/Flights/BrandedFares/BrandsModal";
import ChangeTicket from "@components/Flights/ChangeTicket";
import FlightAdditionalInfo from "@components/Flights/FlightAdditionalInfo";
import BookingFormTips from "@components/Result/BookingFormTips";
import PassengerNotificationsModal from "@components/Result/PassengerNotificationsModal";
import ResultFlightInfo from "@components/Result/ResultFlightInfo";
import ResultLogin from "@components/Result/ResultLogin";
import ResultPromoCodes from "@components/Result/ResultPromoCodes";
import ResultRecapitulation from "@components/Result/ResultRecapitulation";
import DesktopEmptyDiv from "@components/UI/DesktopEmptyDiv";
import FormattedMessage from "@components/UI/FormattedMessage";
import SearchSteps from "@components/UI/Header/SearchSteps";
import IconChevronLeft from "@components/UI/Icons/ChevronLeft";
import StandardLayout from "@components/UI/Layouts/StandardLayout";
import { LegacyModalAlert } from "@components/UI/useModalAlert";
import PageSeo from "@components/Utils/PageSeo";

import { pageDataLayer } from "@lib/dataLayers";
import {
  errorCodesToRedirect,
  formatPassengerRecapitulation,
  routeChangeFallback,
} from "@lib/webFunctions";

import useShareInfo from "../hooks/result/useShareInfo";

// @ts-ignore
const Logger = require("shared/services/Logger");

const { title, brandOfferNotAvailable } = defineMessages({
  title: formattedMessageParams("Result.title"),
  brandOfferNotAvailable: formattedMessageParams(
    "BrandedFares.offerNotAvailable"
  ),
});

const ShareInfoWrapper = ({ children }) => {
  const shareInfo = useShareInfo();
  return children(shareInfo);
};

const getRefs = () => {
  const qd = {};
  if (typeof window !== "undefined" && window.location.search) {
    window.location.search
      .substring(1)
      .split(`&`)
      .forEach((item) => {
        // eslint-disable-next-line prefer-const
        let [k, v] = item.split(`=`);
        v = v && decodeURIComponent(v);

        if (v && v.includes(",")) {
          const values = v.split(",");
          values.forEach((val) => {
            (qd[k] = qd[k] || []).push(val);
          });
        } else {
          (qd[k] = qd[k] || []).push(v);
        }
      });
  }
  const { refs: queryRefs } = qd as { refs?: string[] };
  if (queryRefs === undefined || queryRefs === null) {
    return [];
  }

  return queryRefs.map((reference) => decodeURIComponent(reference));
};

const Result = (props) => {
  const dispatch = useDispatch();
  const { intl, router } = props;

  // Get state directly from Redux
  const frontendSettings = useSelector(
    (state) => state.storage.frontendSettings
  );
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const user = useSelector((state) => state.user);
  const currency = useSelector((state) => state.price.currency);
  const currentLanguage = useSelector(
    (state) => state.requestorConfig.currentLanguage
  );

  const backgroundImageUrl = useSelector(
    (state) => state.requestorConfig.backgroundImageUrl
  );

  const showBrandModal = () => {
    setState((prev) => ({ ...prev, showBrandModal: true }));
  };

  const showCustomError = (messageId: string) => {
    setState((prev) => ({
      ...prev,
      customErrorMessage: intl.formatMessage({ id: messageId }),
    }));
  };

  const showNotFoundError = () => {
    setState((prev) => ({
      ...prev,
      customErrorMessage: intl.formatMessage({
        id: "Alert.flightNotFound",
      }),
    }));

    dispatch(stopUniversalLoading());
  };

  const {
    actualFlightOffer = {},
    componentDidFocus,
    fullPrice,
    refs,
    getPriceWithPromoCode,
    passengerNotificationMessages = { isLoading: true, messages: [] },
  } = useResult({
    getRefs,
    showBrandModal,
    showNotFoundError,
    showCustomError,
  });

  const [state, setState] = useState({
    actualFlightOffer: {},
    ticketingCombinations: [],
    paymentFormMethods: [],
    submitted: false,
    showTermAndConditionsModal: false,
    passengerParams: [],
    customerDetail: {},
    initialPassengerValues: null,
    showVerticalScrollBar: false,
    showHorizontalScrollBar: false,
    showBrandModal: false,
    formData: {
      billing: {
        dic: "",
        ic: "",
        company: "",
        street: "",
        country: "",
        zip: "",
        city: "",
      },
    },
    billingActive: false,
    errorMsg: null,
    errorCode: null,
    customErrorMessage: null,
    showPassengerNotificationModal: !!passengerNotificationMessages.messages
      .length,
  });

  const hideCustomError = () => {
    setState((prev) => ({ ...prev, customErrorMessage: null }));
    goBack();
  };

  const hidePassengerNotification = () => {
    setState((prev) => ({
      ...prev,
      showPassengerNotificationModal: false,
    }));
  };

  const goBack = () => {
    if (state.errorCode && !errorCodesToRedirect.includes(state.errorCode)) {
      setState((prev) => ({
        ...prev,
        errorCode: null,
        customErrorMessage: null,
      }));
      return;
    }

    routeChangeFallback(() => {
      router.push(ROUTES.HOME);
    }, router);

    setState((prev) => ({
      ...prev,
      errorCode: null,
      customErrorMessage: null,
    }));
    router.back();
  };

  const closeErrorModal = () => {
    setState((prev) => ({ ...prev, errorMsg: null }));
    dispatch(stopUniversalLoading());
    goBack();
  };

  useEffect(() => {
    const initPage = async () => {
      pageDataLayer("services", user);
      pageDataLayer("customerInfo", user);

      dispatch(startUniversalLoading());
      await componentDidFocus();

      try {
        Router.prefetch("/booked");

        const SCROLL_TO_CONTENT_TIMEOUT_MS = 40;
        const SCROLL_THRESHOLD_PX = 100;

        setTimeout(() => {
          const currentScrollTop =
            window.pageYOffset || document.documentElement.scrollTop;

          if (currentScrollTop < SCROLL_THRESHOLD_PX) {
            scrollTo(".result-information", { behavior: "smooth" });
          }
        }, SCROLL_TO_CONTENT_TIMEOUT_MS);

        dispatch(stopUniversalLoading());
      } catch (e) {
        Logger.error(e);
        dispatch(stopUniversalLoading());
      }
    };

    initPage();
  }, [dispatch, user, componentDidFocus]);

  useEffect(() => {
    if (
      passengerNotificationMessages.messages.length > 0 &&
      !state.showPassengerNotificationModal
    ) {
      setState((prev) => ({
        ...prev,
        showPassengerNotificationModal: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengerNotificationMessages.messages]);

  // Render
  const bookingDisabled =
    frontendSettings?.dealerCorporateSettings?.enableAnonymousBook ===
      "false" && !isLoggedIn;

  const passengerChangeConditions = actualFlightOffer.TicketChangeConditions
    ? actualFlightOffer.TicketChangeConditions.PassengerChangeConditions
    : actualFlightOffer.PricingDetails?.PricingDetail?.[0]
        ?.TicketChangeConditions?.PassengerChangeConditions || false;

  if (state.showBrandModal && !actualFlightOffer.FlightItinerary) {
    return (
      <LegacyModalAlert
        show={state.showBrandModal}
        onClose={() => {
          setState((prev) => ({ ...prev, showBrandModal: false }));

          const { query } = Router;
          delete query.refs;

          return Router.push({
            pathname: "/results",
            query,
          });
        }}
        title={intl.formatMessage(brandOfferNotAvailable)}
        customTitleClass="branded-fares -error"
        hideButtons
        isCloseButtonHidden={false}
        customContent={
          <BrandsModal
            getRefsForFlightOffer={getRefs}
            flightOffer={actualFlightOffer}
            currency={currency}
            keepBookingClass={false}
          />
        }
      />
    );
  }

  if (!actualFlightOffer.FlightItinerary) {
    return (
      <LegacyModalAlert
        show={!!state.customErrorMessage}
        onClose={goBack}
        message={state.customErrorMessage}
        hideHeader
      />
    );
  }

  const flightStreams = actualFlightOffer.FlightItinerary.FlightStream;

  const formattedPassengerPrices = formatPassengerRecapitulation(
    actualFlightOffer?.AlternativeCurrencyPricingDetails
      ?.AlternativeCurrencyPricingDetail?.[0].FlightPricing
      ?.FlightPassengerPrices?.FlightPassengerPrice ||
      actualFlightOffer?.FlightPassengerPrices?.FlightPassengerPrice ||
      actualFlightOffer.FlightPassengerPrices
  );

  const passengerCount =
    actualFlightOffer.PricingDetails?.PricingDetail?.[0]?.FlightPricing
      ?.FlightPassengerPrices?.FlightPassengerPrice?.length;

  return (
    <div>
      <PageSeo
        title={{
          defaultTitle: intl.formatMessage(title),
        }}
      />
      <div className="search-steps-link-container container">
        <span className="button-result-back" onClick={goBack} role="button">
          <span className="label-button-back-result bold">
            <div className="icon-button-back-result-desktop">
              <IconChevronLeft size={20} />
            </div>
            <FormattedMessage id="SearchSteps.back" />
          </span>
        </span>
        <span
          className="icon-button-back-result-mobile"
          onClick={goBack}
          role="button"
        >
          <IconChevronLeft size={25} />
        </span>
        <SearchSteps page="result" />
        <DesktopEmptyDiv styles={{ width: "60px" }} />
      </div>

      <div className="container">
        {actualFlightOffer.Warnings?.Warning?.length > 0 && (
          <div className="result-warning">
            {actualFlightOffer.Warnings.Warning[0].$t}
          </div>
        )}
        <div className="result" id="result-main-div">
          <div className="result-information">
            <div className={backgroundImageUrl ? "result-box-description" : ""}>
              <h3 className="h3 result-h3">
                <FormattedMessage id="Result.flightInformation" />
              </h3>
              {actualFlightOffer.CombinedOffer === "true" && (
                <span
                  id="two-one-way-detail-text"
                  style={{
                    display: "inline-block",
                    marginLeft: "10px",
                    marginBottom: "15px",
                  }}
                >
                  <FormattedMessage id="FlightOffer.OneWayIndicator.tooltip" />
                </span>
              )}
              {actualFlightOffer.LowCost === "true" && (
                <span
                  id="low-cost-detail-text"
                  style={{
                    display: "inline-block",
                    marginLeft: "10px",
                    marginBottom: "15px",
                  }}
                >
                  <FormattedMessage id="FlightOffer.LowCostIndicator.tooltip" />
                </span>
              )}
            </div>
            <div className="result-box" style={{ paddingTop: "35px" }}>
              {flightStreams.map((flightStream, index) => (
                // @ts-ignore - currentLanguage is needed
                <ResultFlightInfo
                  key={index}
                  index={index}
                  flightOption={flightStream.FlightOption}
                  currentLanguage={currentLanguage}
                />
              ))}
            </div>
            <ShareInfoWrapper>
              {(shareInfo) => (
                <FlightAdditionalInfo
                  passengerCount={passengerCount}
                  selectedCombination={{
                    PricingDetail: {
                      BaggageLimit: actualFlightOffer.PricingDetails
                        ?.PricingDetail?.[0]?.BaggageLimit || {
                        // @ts-ignore - Query type might be string[]
                        DetailsAvailable:
                          router.query?.detailsAvailable ?? "false",
                        // @ts-ignore - Query type might be string[]
                        Quantity: router.query?.baggageLimitQuantity ?? "",
                        // @ts-ignore - Query type might be string[]
                        Weight: router.query?.baggageLimitWeight ?? "",
                        // @ts-ignore - Query type might be string[]
                        Included: router.query?.baggageIncluded ?? "Unknown",
                        BaggageSelectionAvailable: "false",
                      },
                    },
                  }}
                  index={0}
                  getRefsForFlightOffer={getRefs}
                  intl={intl}
                  // @ts-ignore - Query type might be string[]
                  flightSource={router.query?.flightSource ?? null}
                  page="result"
                  shareInfo={shareInfo}
                />
              )}
            </ShareInfoWrapper>
            <div
              className={
                backgroundImageUrl
                  ? "result-form-box-wrapper only-mobile"
                  : "only-mobile"
              }
              id="result-summary-container-mobile"
            >
              <ResultRecapitulation
                passengerPrices={formattedPassengerPrices}
                backgroundImageUrl={backgroundImageUrl}
              />
            </div>
            {passengerChangeConditions && (
              <ChangeTicket
                passengerChangeConditions={passengerChangeConditions}
              />
            )}
            {bookingDisabled ? (
              <ResultLogin />
            ) : (
              <>
                <BookingFormTips backgroundImageUrl={backgroundImageUrl} />

                {/* @ts-ignore - BookingForm type issue */}
                <BookingForm
                  refs={refs}
                  fullPrice={fullPrice}
                  isFlight
                  isHotel={false}
                />

                <div className="only-mobile">
                  <ResultPromoCodes
                    getPriceWithPromoCode={getPriceWithPromoCode}
                    destination="mobile"
                  />
                </div>
              </>
            )}
          </div>
          <div className="result-summary" id="result-summary-container">
            <ResultRecapitulation
              passengerPrices={formattedPassengerPrices}
              backgroundImageUrl={backgroundImageUrl}
            />
            <ResultPromoCodes
              getPriceWithPromoCode={getPriceWithPromoCode}
              destination="desktop"
            />
          </div>
        </div>

        {!bookingDisabled && (
          // @ts-ignore - navigation prop not needed
          <BookingFormSubmit refs={refs} fullPrice={fullPrice} />
        )}
      </div>

      <LegacyModalAlert
        show={state.errorMsg !== null}
        onClose={closeErrorModal}
        message={state.errorMsg}
        hideHeader
      />
      <LegacyModalAlert
        show={!!state.customErrorMessage}
        onClose={hideCustomError}
        message={state.customErrorMessage}
        hideHeader
      />
      <PassengerNotificationsModal
        show={!!state.showPassengerNotificationModal}
        onClose={hidePassengerNotification}
        notifications={passengerNotificationMessages.messages}
      />
    </div>
  );
};

Result.getInitialProps = async (initialProps) => {
  try {
    const { domain } = await setInitialRequestorConfig(initialProps);
    return { domain };
  } catch (e) {
    Logger.error("Error Initial. ", e);
    return {};
  }
};

Result.getLayout = (page, nonce) => {
  const isSpecialOffer =
    page?.props?.router?.query?.isSpecialOffer === "true" ||
    page?.props?.router?.asPath?.includes("isSpecialOffer=true");

  return (
    <StandardLayout
      nonce={nonce}
      hideSearchForm={isSpecialOffer}
      showCondensedHeaderLogoStripe={isSpecialOffer}
    >
      {page}
    </StandardLayout>
  );
};

export default injectIntl(Result);
